"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "groupBy", {
    enumerable: true,
    get: function() {
        return groupBy;
    }
});
const _nocodbsdk = require("nocodb-sdk");
const _aggregation = /*#__PURE__*/ _interop_require_default(require("~/db/aggregation"));
const _pg = require("~/db/aggregations/pg");
const _sqlSanitize = require("~/helpers/sqlSanitize");
const _conditionV2 = /*#__PURE__*/ _interop_require_default(require("~/db/conditionV2"));
const _generateLookupSelectQuery = /*#__PURE__*/ _interop_require_default(require("~/db/generateLookupSelectQuery"));
const _genRollupSelectv2 = /*#__PURE__*/ _interop_require_default(require("~/db/genRollupSelectv2"));
const _catchError = require("~/helpers/catchError");
const _dbHelpers = require("~/helpers/dbHelpers");
const _models = require("~/models");
const _utils = require("~/utils");
const _sqlite3 = require("~/db/aggregations/sqlite3");
const _ncconfig = require("~/utils/nc-config");
function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {}, sourceKeys = Object.getOwnPropertyNames(source), key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
        target[key] = source[key];
    }
    return target;
}
// PR review fix #2: Shared helper for UUID group-by to avoid 4x code duplication.
// Casts UUID to text on PostgreSQL (avoids type mismatch), passes through on other DBs.
const buildUuidGroupBySelector = ({ baseModel, columnName, alias })=>{
    if (baseModel.isPg) {
        return baseModel.dbDriver.raw('(??)::text as ??', [
            columnName,
            alias
        ]);
    }
    return baseModel.dbDriver.raw('?? as ??', [
        columnName,
        alias
    ]);
};
// Returns a SQL expression that converts blank (null or '') values to NULL
const sqlNullIfBlank = ({ baseModel, columnName, isStringType = false })=>{
    if (baseModel.isPg && !isStringType) {
        return baseModel.dbDriver.raw(`CASE 
        WHEN (pg_typeof(:column:) = 'text'::regtype 
          OR pg_typeof(:column:) = 'varchar'::regtype 
          OR pg_typeof(:column:) = 'char'::regtype) 
          AND (:column:)::text = '' 
        THEN NULL
        ELSE :column:
      END`, {
            column: columnName
        });
    }
    return baseModel.dbDriver.raw(`NULLIF(??, '')`, [
        columnName
    ]);
};
const groupBy = (baseModel, logger)=>{
    const list = async (args)=>{
        var _baseModel_model_primaryKey;
        const _baseModel__getListArgs = baseModel._getListArgs(args), { where } = _baseModel__getListArgs, rest = _object_without_properties_loose(_baseModel__getListArgs, [
            "where"
        ]);
        args.column_name = args.column_name || '';
        const subGroupColumnName = args.subGroupColumnName;
        const columns = await baseModel.model.getColumns(baseModel.context);
        const groupByColumns = {};
        const selectors = [];
        const groupBySelectors = [];
        const getAlias = (0, _utils.getAliasGenerator)('__nc_gb');
        const _subGroupColumn = columns.find((c)=>c.title === subGroupColumnName || c.column_name === subGroupColumnName);
        const processColumn = async (col, isSubGroup = false)=>{
            let column = columns.find((c)=>c.column_name === col || c.title === col);
            if (!column) {
                _catchError.NcError.get(baseModel.context).fieldNotFound(col);
            }
            // if qrCode or Barcode replace it with value column nd keep the alias
            if ([
                _nocodbsdk.UITypes.QrCode,
                _nocodbsdk.UITypes.Barcode
            ].includes(column.uidt)) {
                column = new _models.Column(_extends({}, await column.getColOptions(baseModel.context).then((col)=>col.getValueColumn(baseModel.context)), {
                    asId: column.id
                }));
            }
            const alias = (0, _dbHelpers.getAs)(column);
            if (!isSubGroup) {
                groupByColumns[alias] = column;
                groupBySelectors.push(alias);
            }
            let columnQuery;
            switch(column.uidt){
                case _nocodbsdk.UITypes.Attachment:
                    _catchError.NcError.get(baseModel.context).badRequest('Group by using attachment column is not supported');
                    break;
                case _nocodbsdk.UITypes.Button:
                    _catchError.NcError.get(baseModel.context).badRequest('Group by using Button column is not supported');
                    break;
                case _nocodbsdk.UITypes.Links:
                case _nocodbsdk.UITypes.Rollup:
                    columnQuery = (await (0, _genRollupSelectv2.default)({
                        baseModelSqlv2: baseModel,
                        knex: baseModel.dbDriver,
                        columnOptions: await column.getColOptions(baseModel.context)
                    })).builder;
                    if (!isSubGroup) {
                        selectors.push(columnQuery.as(alias));
                    }
                    break;
                case _nocodbsdk.UITypes.Formula:
                    try {
                        const _selectQb = await baseModel.getSelectQueryBuilderForFormula(column);
                        columnQuery = _selectQb.builder;
                        // if postgres and formula output defined as string then cast to text for consistent output
                        if (baseModel.isPg && column.colOptions.getParsedTree().dataType === _nocodbsdk.FormulaDataTypes.STRING) {
                            columnQuery = sqlNullIfBlank({
                                columnName: baseModel.dbDriver.raw(`??::text`, [
                                    columnQuery
                                ]),
                                baseModel,
                                isStringType: true
                            });
                        }
                    } catch (e) {
                        logger.log(e);
                        columnQuery = baseModel.dbDriver.raw(`'ERR'`);
                    }
                    if (!isSubGroup) {
                        selectors.push(baseModel.dbDriver.raw(`?? as ??`, [
                            columnQuery,
                            alias
                        ]));
                    }
                    break;
                case _nocodbsdk.UITypes.Lookup:
                case _nocodbsdk.UITypes.LinkToAnotherRecord:
                    {
                        const lookupQb = await (0, _generateLookupSelectQuery.default)({
                            baseModelSqlv2: baseModel,
                            column,
                            alias: null,
                            model: baseModel.model,
                            getAlias
                        });
                        columnQuery = baseModel.dbDriver.raw(lookupQb.builder).wrap('(', ')');
                        if (!isSubGroup) {
                            selectors.push(baseModel.dbDriver.raw(`?? as ??`, [
                                columnQuery,
                                alias
                            ]));
                        }
                        break;
                    }
                case _nocodbsdk.UITypes.CreatedTime:
                case _nocodbsdk.UITypes.LastModifiedTime:
                case _nocodbsdk.UITypes.DateTime:
                    {
                        const columnName = await (0, _dbHelpers.getColumnName)(baseModel.context, column, columns);
                        if (baseModel.dbDriver.clientType() === 'pg') {
                            columnQuery = baseModel.dbDriver.raw("date_trunc('minute', ??) + interval '0 seconds'", [
                                columnName
                            ]);
                        } else if (baseModel.dbDriver.clientType() === 'mysql' || baseModel.dbDriver.clientType() === 'mysql2') {
                            columnQuery = baseModel.dbDriver.raw("DATE_SUB(CONVERT_TZ(??, @@GLOBAL.time_zone, '+00:00'), INTERVAL SECOND(??) SECOND)", [
                                columnName,
                                columnName
                            ]);
                        } else if (baseModel.dbDriver.clientType() === 'sqlite3') {
                            columnQuery = baseModel.dbDriver.raw(`strftime('%Y-%m-%d %H:%M:00', ??)`, [
                                columnName
                            ]);
                        } else {
                            columnQuery = baseModel.dbDriver.raw('DATE(??)', [
                                columnName
                            ]);
                        }
                        if (!isSubGroup) {
                            selectors.push(baseModel.dbDriver.raw(`?? as ??`, [
                                columnQuery,
                                alias
                            ]));
                        }
                        break;
                    }
                case _nocodbsdk.UITypes.JSON:
                    {
                        if (baseModel.dbDriver.clientType() === 'pg') {
                            const defaultColumnName = await (0, _dbHelpers.getColumnName)(baseModel.context, column, columns);
                            columnQuery = baseModel.dbDriver.raw('(??)::jsonb', [
                                defaultColumnName
                            ]);
                            if (!isSubGroup) {
                                selectors.push(baseModel.dbDriver.raw(`?? as ??`, [
                                    columnQuery,
                                    alias
                                ]));
                            }
                        }
                        break;
                    }
                case _nocodbsdk.UITypes.UUID:
                    {
                        // PR review fix #2: Cast UUID to text on PG to avoid type mismatch
                        const uuidColumnName = await (0, _dbHelpers.getColumnName)(baseModel.context, column, columns);
                        if (baseModel.isPg) {
                            columnQuery = baseModel.dbDriver.raw('(??)::text', [
                                uuidColumnName
                            ]);
                        } else {
                            columnQuery = baseModel.dbDriver.raw('??', [
                                uuidColumnName
                            ]);
                        }
                        if (!isSubGroup) {
                            selectors.push(baseModel.dbDriver.raw(`?? as ??`, [
                                columnQuery,
                                alias
                            ]));
                        }
                        break;
                    }
                default:
                    {
                        const defaultColumnName = await (0, _dbHelpers.getColumnName)(baseModel.context, column, columns);
                        const defaultColumnNameQb = sqlNullIfBlank({
                            columnName: defaultColumnName,
                            baseModel
                        });
                        columnQuery = baseModel.dbDriver.raw('??', [
                            defaultColumnNameQb
                        ]);
                        if (!isSubGroup) {
                            selectors.push(baseModel.dbDriver.raw(`?? as ??`, [
                                defaultColumnNameQb,
                                alias
                            ]));
                        }
                        break;
                    }
            }
            return columnQuery.toQuery(); // Always return the raw query for subgroup
        };
        await Promise.all(args.column_name.split(',').map((col)=>processColumn(col)));
        const qb = baseModel.dbDriver(baseModel.tnPath);
        // get aggregated count of each group
        qb.count(`${((_baseModel_model_primaryKey = baseModel.model.primaryKey) == null ? void 0 : _baseModel_model_primaryKey.column_name) || '*'} as count`);
        if (subGroupColumnName) {
            const subGroupQuery = await processColumn(subGroupColumnName, true);
            qb.select(baseModel.dbDriver.raw(`COUNT(DISTINCT COALESCE(${sqlNullIfBlank({
                columnName: baseModel.dbDriver.raw(baseModel.isPg ? '(??)::text' : '??', [
                    baseModel.dbDriver.raw(subGroupQuery)
                ]),
                baseModel,
                isStringType: true
            })}, '__null__')) as ??`, [
                '__sub_group_count__'
            ]));
        }
        qb.select(...selectors);
        if (+(rest == null ? void 0 : rest.shuffle)) {
            await baseModel.shuffle({
                qb
            });
        }
        const aliasColObjMap = await baseModel.model.getAliasColObjMap(baseModel.context, columns);
        let sorts = (0, _dbHelpers.extractSortsObject)(baseModel.context, rest == null ? void 0 : rest.sort, aliasColObjMap);
        const { filters: filterObj } = (0, _nocodbsdk.extractFilterFromXwhere)(baseModel.context, where, aliasColObjMap);
        await (0, _conditionV2.default)(baseModel, [
            ...baseModel.viewId ? [
                new _models.Filter({
                    children: await _models.Filter.rootFilterList(baseModel.context, {
                        viewId: baseModel.viewId
                    }) || [],
                    is_group: true
                })
            ] : [],
            new _models.Filter({
                children: args.filterArr || [],
                is_group: true,
                logical_op: 'and'
            }),
            new _models.Filter({
                children: filterObj,
                is_group: true,
                logical_op: 'and'
            })
        ], qb);
        if (!sorts) {
            var _args_sortArr;
            if ((_args_sortArr = args.sortArr) == null ? void 0 : _args_sortArr.length) {
                sorts = args.sortArr;
            } else if (baseModel.viewId) {
                sorts = await _models.Sort.list(baseModel.context, {
                    viewId: baseModel.viewId
                });
            }
        }
        // group by using the column aliases
        qb.groupBy(...groupBySelectors);
        // Add HAVING clause to filter groups by minimum count (e.g., count > 1 for duplicates only)
        if (args.minCount !== undefined && args.minCount > 0) {
            var _baseModel_model_primaryKey1;
            qb.havingRaw('COUNT(??) >= ?', [
                ((_baseModel_model_primaryKey1 = baseModel.model.primaryKey) == null ? void 0 : _baseModel_model_primaryKey1.column_name) || '*',
                args.minCount
            ]);
        }
        // Wrap in a CTE to allow referencing grouped/aliased columns in subqueries (esp. for Postgres)
        // We'll use: WITH grouped AS (<qb>) SELECT ... FROM grouped g
        const outerQb = baseModel.dbDriver.with('grouped', qb.clone()).select('*').from({
            g: 'grouped'
        });
        if (!_ncconfig.NC_DISABLE_GROUP_BY_LIMIT) {
            (0, _dbHelpers.applyPaginate)(outerQb, rest);
        }
        // Apply order by on the outer query, referencing g.<alias>
        for (const sort of sorts || []){
            if (!groupByColumns[sort.fk_column_id]) {
                continue;
            }
            const column = groupByColumns[sort.fk_column_id];
            if ([
                _nocodbsdk.UITypes.User,
                _nocodbsdk.UITypes.CreatedBy,
                _nocodbsdk.UITypes.LastModifiedBy
            ].includes(column.uidt)) {
                const baseUsers = await _models.BaseUser.getUsersList(baseModel.context, {
                    base_id: column.base_id,
                    include_internal_user: true
                });
                const groupedCol = (0, _dbHelpers.getAs)(column);
                const groupedColQb = sqlNullIfBlank({
                    columnName: baseModel.dbDriver.raw('??.??', [
                        'g',
                        groupedCol
                    ]),
                    baseModel,
                    isStringType: true
                });
                let finalStatement = '';
                if (baseModel.dbDriver.clientType() === 'pg') {
                    finalStatement = `(${(0, _pg.replaceDelimitedWithKeyValuePg)({
                        knex: baseModel.dbDriver,
                        needleColumn: groupedColQb,
                        stack: baseUsers.map((user)=>({
                                key: user.id,
                                value: user.display_name || user.email
                            }))
                    })})`;
                } else if (baseModel.dbDriver.clientType() === 'sqlite3') {
                    finalStatement = `(${(0, _sqlite3.replaceDelimitedWithKeyValueSqlite3)({
                        knex: baseModel.dbDriver,
                        needleColumn: groupedColQb,
                        stack: baseUsers.map((user)=>({
                                key: user.id,
                                value: user.display_name || user.email
                            }))
                    })})`;
                } else {
                    finalStatement = baseUsers.reduce((acc, user)=>{
                        const qbReplace = baseModel.dbDriver.raw(`REPLACE(${acc}, ?, ?)`, [
                            user.id,
                            user.display_name || user.email
                        ]);
                        return qbReplace.toQuery();
                    }, groupedColQb.toQuery());
                }
                if (![
                    'asc',
                    'desc'
                ].includes(sort.direction)) {
                    outerQb.orderBy('g.count', sort.direction === 'count-desc' ? 'desc' : 'asc', sort.direction === 'count-desc' ? 'LAST' : 'FIRST');
                    outerQb.orderBy((0, _sqlSanitize.sanitize)(baseModel.dbDriver.raw(finalStatement)), sort.direction, 'FIRST');
                } else {
                    outerQb.orderBy((0, _sqlSanitize.sanitize)(baseModel.dbDriver.raw(finalStatement)), sort.direction, sort.direction === 'desc' ? 'LAST' : 'FIRST');
                }
            } else {
                if (![
                    'asc',
                    'desc'
                ].includes(sort.direction)) {
                    outerQb.orderBy('g.count', sort.direction === 'count-desc' ? 'desc' : 'asc', sort.direction === 'count-desc' ? 'LAST' : 'FIRST');
                    outerQb.orderBy(baseModel.dbDriver.raw('??.??', [
                        'g',
                        (0, _dbHelpers.getAs)(column)
                    ]), sort.direction, 'FIRST');
                } else {
                    outerQb.orderBy(baseModel.dbDriver.raw('??.??', [
                        'g',
                        (0, _dbHelpers.getAs)(column)
                    ]), sort.direction, sort.direction === 'desc' ? 'LAST' : 'FIRST');
                }
            }
        }
        return await baseModel.execAndParse(baseModel.dbDriver.from(baseModel.dbDriver.raw(outerQb).wrap('(', ') __nc_group_alias')));
    };
    const count = async (args)=>{
        var _baseModel_model_primaryKey, _this;
        const { where } = baseModel._getListArgs(args);
        args.column_name = args.column_name || '';
        const selectors = [];
        const groupBySelectors = [];
        const getAlias = (0, _utils.getAliasGenerator)('__nc_gb');
        const columns = await baseModel.model.getColumns(baseModel.context);
        // todo: refactor and avoid duplicate code
        await Promise.all(args.column_name.split(',').map(async (col)=>{
            let column = columns.find((c)=>c.column_name === col || c.title === col);
            if (!column) {
                _catchError.NcError.get(baseModel.context).fieldNotFound(col);
            }
            // if qrCode or Barcode replace it with value column nd keep the alias
            if ([
                _nocodbsdk.UITypes.QrCode,
                _nocodbsdk.UITypes.Barcode
            ].includes(column.uidt)) column = new _models.Column(_extends({}, await column.getColOptions(baseModel.context).then((col)=>col.getValueColumn(baseModel.context)), {
                asId: column.id
            }));
            switch(column.uidt){
                case _nocodbsdk.UITypes.Attachment:
                    _catchError.NcError.get(baseModel.context).badRequest('Group by using attachment column is not supported');
                    break;
                case _nocodbsdk.UITypes.Button:
                    {
                        _catchError.NcError.get(baseModel.context).badRequest('Group by using Button column is not supported');
                        break;
                    }
                case _nocodbsdk.UITypes.Rollup:
                case _nocodbsdk.UITypes.Links:
                    selectors.push((await (0, _genRollupSelectv2.default)({
                        baseModelSqlv2: baseModel,
                        // tn: baseModel.title,
                        knex: baseModel.dbDriver,
                        // column,
                        // alias,
                        columnOptions: await column.getColOptions(baseModel.context)
                    })).builder.as((0, _dbHelpers.getAs)(column)));
                    groupBySelectors.push((0, _dbHelpers.getAs)(column));
                    break;
                case _nocodbsdk.UITypes.Formula:
                    {
                        let selectQb;
                        try {
                            const _selectQb = await baseModel.getSelectQueryBuilderForFormula(column);
                            selectQb = baseModel.dbDriver.raw(`?? as ??`, [
                                sqlNullIfBlank({
                                    columnName: _selectQb.builder,
                                    baseModel
                                }),
                                (0, _dbHelpers.getAs)(column)
                            ]);
                        } catch (e) {
                            logger.log(e);
                            // return dummy select
                            selectQb = baseModel.dbDriver.raw(`'ERR' as ??`, [
                                (0, _dbHelpers.getAs)(column)
                            ]);
                        }
                        selectors.push(selectQb);
                        groupBySelectors.push((0, _dbHelpers.getAs)(column));
                        break;
                    }
                case _nocodbsdk.UITypes.Lookup:
                case _nocodbsdk.UITypes.LinkToAnotherRecord:
                    {
                        const _selectQb = await (0, _generateLookupSelectQuery.default)({
                            baseModelSqlv2: baseModel,
                            column,
                            alias: null,
                            model: baseModel.model,
                            getAlias
                        });
                        const selectQb = baseModel.dbDriver.raw(`?? as ??`, [
                            baseModel.dbDriver.raw(_selectQb.builder).wrap('(', ')'),
                            (0, _dbHelpers.getAs)(column)
                        ]);
                        selectors.push(selectQb);
                        groupBySelectors.push((0, _dbHelpers.getAs)(column));
                    }
                    break;
                case _nocodbsdk.UITypes.CreatedTime:
                case _nocodbsdk.UITypes.LastModifiedTime:
                case _nocodbsdk.UITypes.DateTime:
                    {
                        const columnName = await (0, _dbHelpers.getColumnName)(baseModel.context, column, columns);
                        // ignore seconds part in datetime and group
                        if (baseModel.dbDriver.clientType() === 'pg') {
                            selectors.push(baseModel.dbDriver.raw("date_trunc('minute', ??) + interval '0 seconds' as ??", [
                                columnName,
                                (0, _dbHelpers.getAs)(column)
                            ]));
                        } else if (baseModel.dbDriver.clientType() === 'mysql' || baseModel.dbDriver.clientType() === 'mysql2') {
                            selectors.push(baseModel.dbDriver.raw("CONVERT_TZ(DATE_SUB(??, INTERVAL SECOND(??) SECOND), @@GLOBAL.time_zone, '+00:00') as ??", [
                                columnName,
                                columnName,
                                (0, _dbHelpers.getAs)(column)
                            ]));
                        } else if (baseModel.dbDriver.clientType() === 'sqlite3') {
                            selectors.push(baseModel.dbDriver.raw(`strftime ('%Y-%m-%d %H:%M:00',:column:) ||
  (
  CASE WHEN substr(:column:, 20, 1) = '+' THEN
    printf ('+%s:',
    substr(:column:, 21, 2)) || printf ('%s',
    substr(:column:, 24, 2))
  WHEN substr(:column:, 20, 1) = '-' THEN
    printf ('-%s:',
    substr(:column:, 21, 2)) || printf ('%s',
    substr(:column:, 24, 2))
  ELSE
    '+00:00'
  END) as :id:`, {
                                column: columnName,
                                id: (0, _dbHelpers.getAs)(column)
                            }));
                        } else {
                            selectors.push(baseModel.dbDriver.raw('DATE(??) as ??', [
                                columnName,
                                (0, _dbHelpers.getAs)(column)
                            ]));
                        }
                        groupBySelectors.push((0, _dbHelpers.getAs)(column));
                    }
                    break;
                case _nocodbsdk.UITypes.JSON:
                    {
                        if (baseModel.dbDriver.clientType() === 'pg') {
                            const columnName = await (0, _dbHelpers.getColumnName)(baseModel.context, column, columns);
                            selectors.push(baseModel.dbDriver.raw('(??)::jsonb as ??', [
                                columnName,
                                (0, _dbHelpers.getAs)(column)
                            ]));
                            groupBySelectors.push((0, _dbHelpers.getAs)(column));
                        }
                        break;
                    }
                case _nocodbsdk.UITypes.UUID:
                    {
                        // PR review fix #2: use shared helper to cast UUID to text on PG
                        const columnName = await (0, _dbHelpers.getColumnName)(baseModel.context, column, columns);
                        selectors.push(buildUuidGroupBySelector({
                            baseModel,
                            columnName,
                            alias: (0, _dbHelpers.getAs)(column)
                        }));
                        groupBySelectors.push((0, _dbHelpers.getAs)(column));
                        break;
                    }
                default:
                    {
                        const columnName = await (0, _dbHelpers.getColumnName)(baseModel.context, column, columns);
                        selectors.push(baseModel.dbDriver.raw('?? as ??', [
                            sqlNullIfBlank({
                                columnName,
                                baseModel
                            }),
                            (0, _dbHelpers.getAs)(column)
                        ]));
                        groupBySelectors.push((0, _dbHelpers.getAs)(column));
                    }
                    break;
            }
        }));
        // Build the group-by query
        const qb = baseModel.dbDriver(baseModel.tnPath);
        qb.count(`${((_baseModel_model_primaryKey = baseModel.model.primaryKey) == null ? void 0 : _baseModel_model_primaryKey.column_name) || '*'} as count`);
        qb.select(...selectors);
        const aliasColObjMap = await baseModel.model.getAliasColObjMap(baseModel.context, columns);
        const { filters: filterObj } = (0, _nocodbsdk.extractFilterFromXwhere)(baseModel.context, where, aliasColObjMap);
        await (0, _conditionV2.default)(baseModel, [
            ...baseModel.viewId ? [
                new _models.Filter({
                    children: await _models.Filter.rootFilterList(baseModel.context, {
                        viewId: baseModel.viewId
                    }) || [],
                    is_group: true
                })
            ] : [],
            new _models.Filter({
                children: args.filterArr || [],
                is_group: true,
                logical_op: 'and'
            }),
            new _models.Filter({
                children: filterObj,
                is_group: true,
                logical_op: 'and'
            })
        ], qb);
        qb.groupBy(...groupBySelectors);
        // Add HAVING clause to filter groups by minimum count (e.g., count > 1 for duplicates only)
        if (args.minCount !== undefined && args.minCount > 0) {
            var _baseModel_model_primaryKey1;
            qb.havingRaw('COUNT(??) >= ?', [
                ((_baseModel_model_primaryKey1 = baseModel.model.primaryKey) == null ? void 0 : _baseModel_model_primaryKey1.column_name) || '*',
                args.minCount
            ]);
        }
        // Wrap in a CTE so that we can reference grouped columns safely in all engines
        // SELECT COUNT(*) FROM (WITH grouped AS (<qb>) SELECT * FROM grouped g) sub
        const groupedCte = baseModel.dbDriver.with('grouped', qb.clone()).select('*').from({
            g: 'grouped'
        });
        const qbP = baseModel.dbDriver.count('*', {
            as: 'count'
        }).from(groupedCte.as('sub'));
        return (_this = await baseModel.execAndParse(qbP, null, {
            raw: true,
            first: true
        })) == null ? void 0 : _this.count;
    };
    const bulkCount = async (args, bulkFilterList, _view)=>{
        try {
            const columns = await baseModel.model.getColumns(baseModel.context);
            const aliasColObjMap = await baseModel.model.getAliasColObjMap(baseModel.context, columns);
            const selectors = [];
            const viewFilterList = await _models.Filter.rootFilterList(baseModel.context, {
                viewId: baseModel.viewId
            });
            if (!(bulkFilterList == null ? void 0 : bulkFilterList.length)) {
                return _catchError.NcError.get(baseModel.context).badRequest('bulkFilterList is required');
            }
            for (const f of bulkFilterList){
                var _baseModel_model_primaryKey;
                const _baseModel__getListArgs = baseModel._getListArgs(f), { where } = _baseModel__getListArgs, rest = _object_without_properties_loose(_baseModel__getListArgs, [
                    "where"
                ]);
                const groupBySelectors = [];
                const groupByColumns = {};
                const getAlias = (0, _utils.getAliasGenerator)('__nc_gb');
                const { filters: groupFilter } = (0, _nocodbsdk.extractFilterFromXwhere)(baseModel.context, f.where, aliasColObjMap);
                const tQb = baseModel.dbDriver(baseModel.tnPath);
                const colSelectors = [];
                await Promise.all(rest.column_name.split(',').map(async (col)=>{
                    let column = columns.find((c)=>c.column_name === col || c.title === col);
                    // if qrCode or Barcode replace it with value column nd keep the alias
                    if ([
                        _nocodbsdk.UITypes.QrCode,
                        _nocodbsdk.UITypes.Barcode
                    ].includes(column.uidt)) {
                        column = new _models.Column(_extends({}, await column.getColOptions(baseModel.context).then((col)=>col.getValueColumn(baseModel.context)), {
                            asId: column.id
                        }));
                    }
                    groupByColumns[(0, _dbHelpers.getAs)(column)] = column;
                    switch(column.uidt){
                        case _nocodbsdk.UITypes.Attachment:
                            _catchError.NcError.get(baseModel.context).badRequest('Group by using attachment column is not supported');
                            break;
                        case _nocodbsdk.UITypes.Button:
                            {
                                _catchError.NcError.get(baseModel.context).badRequest('Group by using Button column is not supported');
                                break;
                            }
                        case _nocodbsdk.UITypes.Links:
                        case _nocodbsdk.UITypes.Rollup:
                            colSelectors.push((await (0, _genRollupSelectv2.default)({
                                baseModelSqlv2: baseModel,
                                knex: baseModel.dbDriver,
                                columnOptions: await column.getColOptions(baseModel.context)
                            })).builder.as((0, _dbHelpers.getAs)(column)));
                            groupBySelectors.push((0, _dbHelpers.getAs)(column));
                            break;
                        case _nocodbsdk.UITypes.Formula:
                            {
                                let selectQb;
                                try {
                                    const _selectQb = await baseModel.getSelectQueryBuilderForFormula(column);
                                    selectQb = baseModel.dbDriver.raw(`?? as ??`, [
                                        _selectQb.builder,
                                        (0, _dbHelpers.getAs)(column)
                                    ]);
                                } catch (e) {
                                    console.log(e);
                                    selectQb = baseModel.dbDriver.raw(`'ERR' as ??`, [
                                        (0, _dbHelpers.getAs)(column)
                                    ]);
                                }
                                colSelectors.push(selectQb);
                                groupBySelectors.push((0, _dbHelpers.getAs)(column));
                                break;
                            }
                        case _nocodbsdk.UITypes.Lookup:
                        case _nocodbsdk.UITypes.LinkToAnotherRecord:
                            {
                                const _selectQb = await (0, _generateLookupSelectQuery.default)({
                                    baseModelSqlv2: baseModel,
                                    column,
                                    alias: null,
                                    model: baseModel.model,
                                    getAlias
                                });
                                const selectQb = baseModel.dbDriver.raw(`?? as ??`, [
                                    baseModel.dbDriver.raw(_selectQb.builder).wrap('(', ')'),
                                    (0, _dbHelpers.getAs)(column)
                                ]);
                                colSelectors.push(selectQb);
                                groupBySelectors.push((0, _dbHelpers.getAs)(column));
                                break;
                            }
                        case _nocodbsdk.UITypes.DateTime:
                        case _nocodbsdk.UITypes.CreatedTime:
                        case _nocodbsdk.UITypes.LastModifiedTime:
                            {
                                const columnName = await (0, _dbHelpers.getColumnName)(baseModel.context, column, columns);
                                // ignore seconds part in datetime and group
                                if (baseModel.dbDriver.clientType() === 'pg') {
                                    colSelectors.push(baseModel.dbDriver.raw("date_trunc('minute', ??) + interval '0 seconds' as ??", [
                                        columnName,
                                        (0, _dbHelpers.getAs)(column)
                                    ]));
                                } else if (baseModel.dbDriver.clientType() === 'mysql' || baseModel.dbDriver.clientType() === 'mysql2') {
                                    colSelectors.push(// baseModel.dbDriver.raw('??::date as ??', [columnName, getAs(column)]),
                                    baseModel.dbDriver.raw("DATE_SUB(CONVERT_TZ(??, @@GLOBAL.time_zone, '+00:00'), INTERVAL SECOND(??) SECOND) as ??", [
                                        columnName,
                                        columnName,
                                        (0, _dbHelpers.getAs)(column)
                                    ]));
                                } else if (baseModel.dbDriver.clientType() === 'sqlite3') {
                                    colSelectors.push(baseModel.dbDriver.raw(`strftime ('%Y-%m-%d %H:%M:00',:column:) ||
  (
   CASE WHEN substr(:column:, 20, 1) = '+' THEN
    printf ('+%s:',
     substr(:column:, 21, 2)) || printf ('%s',
     substr(:column:, 24, 2))
   WHEN substr(:column:, 20, 1) = '-' THEN
    printf ('-%s:',
     substr(:column:, 21, 2)) || printf ('%s',
     substr(:column:, 24, 2))
   ELSE
    '+00:00'
   END) AS :id:`, {
                                        column: columnName,
                                        id: (0, _dbHelpers.getAs)(column)
                                    }));
                                } else {
                                    colSelectors.push(baseModel.dbDriver.raw('DATE(??) as ??', [
                                        columnName,
                                        (0, _dbHelpers.getAs)(column)
                                    ]));
                                }
                                groupBySelectors.push((0, _dbHelpers.getAs)(column));
                            }
                            break;
                        case _nocodbsdk.UITypes.UUID:
                            {
                                // PR review fix #2: use shared helper to cast UUID to text on PG
                                const columnName = await (0, _dbHelpers.getColumnName)(baseModel.context, column, columns);
                                colSelectors.push(buildUuidGroupBySelector({
                                    baseModel,
                                    columnName,
                                    alias: (0, _dbHelpers.getAs)(column)
                                }));
                                groupBySelectors.push((0, _dbHelpers.getAs)(column));
                                break;
                            }
                        default:
                            {
                                const columnName = await (0, _dbHelpers.getColumnName)(baseModel.context, column, columns);
                                colSelectors.push(baseModel.dbDriver.raw('?? as ??', [
                                    columnName,
                                    (0, _dbHelpers.getAs)(column)
                                ]));
                                groupBySelectors.push((0, _dbHelpers.getAs)(column));
                                break;
                            }
                    }
                }));
                // get aggregated count of each group
                tQb.count(`${((_baseModel_model_primaryKey = baseModel.model.primaryKey) == null ? void 0 : _baseModel_model_primaryKey.column_name) || '*'} as count`);
                tQb.select(...colSelectors);
                if (+(rest == null ? void 0 : rest.shuffle)) {
                    await baseModel.shuffle({
                        qb: tQb
                    });
                }
                await (0, _conditionV2.default)(baseModel, [
                    ...baseModel.viewId ? [
                        new _models.Filter({
                            children: viewFilterList || [],
                            is_group: true
                        })
                    ] : [],
                    new _models.Filter({
                        children: rest.filterArr || [],
                        is_group: true,
                        logical_op: 'and'
                    }),
                    new _models.Filter({
                        children: (0, _nocodbsdk.extractFilterFromXwhere)(baseModel.context, where, aliasColObjMap).filters,
                        is_group: true,
                        logical_op: 'and'
                    }),
                    new _models.Filter({
                        children: groupFilter,
                        is_group: true,
                        logical_op: 'and'
                    }),
                    new _models.Filter({
                        children: args.filterArr || [],
                        is_group: true,
                        logical_op: 'and'
                    })
                ], tQb);
                tQb.groupBy(...groupBySelectors);
                const count = baseModel.dbDriver.count('*', {
                    as: 'count'
                }).from(tQb.as('groupby'));
                let subQuery;
                switch(baseModel.dbDriver.client.config.client){
                    case 'pg':
                        subQuery = baseModel.dbDriver.select(baseModel.dbDriver.raw(`json_build_object('count', "count") as ??`, [
                            getAlias()
                        ])).from(count.as(getAlias()));
                        selectors.push(baseModel.dbDriver.raw(`(??) as ??`, [
                            subQuery,
                            `${f.alias}`
                        ]));
                        break;
                    case 'mysql2':
                        subQuery = baseModel.dbDriver.select(baseModel.dbDriver.raw(`JSON_OBJECT('count', \`count\`)`)).from(count.as(getAlias()));
                        selectors.push(baseModel.dbDriver.raw(`(??) as ??`, [
                            subQuery,
                            `${f.alias}`
                        ]));
                        break;
                    case 'sqlite3':
                        subQuery = baseModel.dbDriver.select(baseModel.dbDriver.raw(`json_object('count', "count") as ??`, [
                            f.alias
                        ])).from(count.as(getAlias()));
                        selectors.push(baseModel.dbDriver.raw(`(??) as ??`, [
                            subQuery,
                            `${f.alias}`
                        ]));
                        break;
                    default:
                        _catchError.NcError.get(baseModel.context).notImplemented('This database does not support bulk groupBy count');
                }
            }
            const qb = baseModel.dbDriver(baseModel.tnPath);
            qb.select(...selectors).limit(1);
            return await baseModel.execAndParse(qb, null, {
                raw: true,
                first: true
            });
        } catch (e) {
            console.log(e);
        }
    };
    const bulkList = async (args, bulkFilterList, _view)=>{
        const columns = await baseModel.model.getColumns(baseModel.context);
        const aliasColObjMap = await baseModel.model.getAliasColObjMap(baseModel.context, columns);
        const selectors = [];
        const viewFilterList = await _models.Filter.rootFilterList(baseModel.context, {
            viewId: baseModel.viewId
        });
        try {
            if (!(bulkFilterList == null ? void 0 : bulkFilterList.length)) {
                return _catchError.NcError.get(baseModel.context).badRequest('bulkFilterList is required');
            }
            for (const f of bulkFilterList){
                var _baseModel_model_primaryKey;
                const _baseModel__getListArgs = baseModel._getListArgs(f), { where } = _baseModel__getListArgs, rest = _object_without_properties_loose(_baseModel__getListArgs, [
                    "where"
                ]);
                const groupBySelectors = [];
                const groupByColumns = {};
                const getAlias = (0, _utils.getAliasGenerator)('__nc_gb');
                const { filters: groupFilter } = (0, _nocodbsdk.extractFilterFromXwhere)(baseModel.context, f == null ? void 0 : f.where, aliasColObjMap);
                let groupSort = (0, _dbHelpers.extractSortsObject)(baseModel.context, rest == null ? void 0 : rest.sort, aliasColObjMap);
                const tQb = baseModel.dbDriver(baseModel.tnPath);
                const colSelectors = [];
                const colIds = rest.column_name.split(',').map((col)=>{
                    const column = columns.find((c)=>c.column_name === col || c.title === col);
                    if (!column) {
                        _catchError.NcError.get(baseModel.context).fieldNotFound(col);
                    }
                    return column == null ? void 0 : column.id;
                }).join('_');
                await Promise.all(rest.column_name.split(',').map(async (col)=>{
                    let column = columns.find((c)=>c.column_name === col || c.title === col);
                    // if qrCode or Barcode replace it with value column nd keep the alias
                    if ([
                        _nocodbsdk.UITypes.QrCode,
                        _nocodbsdk.UITypes.Barcode
                    ].includes(column.uidt)) {
                        column = new _models.Column(_extends({}, await column.getColOptions(baseModel.context).then((col)=>col.getValueColumn(baseModel.context)), {
                            asId: column.id
                        }));
                    }
                    groupByColumns[(0, _dbHelpers.getAs)(column)] = column;
                    switch(column.uidt){
                        case _nocodbsdk.UITypes.Attachment:
                            _catchError.NcError.get(baseModel.context).badRequest('Group by using attachment column is not supported');
                            break;
                        case _nocodbsdk.UITypes.Button:
                            {
                                _catchError.NcError.get(baseModel.context).badRequest('Group by using Button column is not supported');
                                break;
                            }
                        case _nocodbsdk.UITypes.Links:
                        case _nocodbsdk.UITypes.Rollup:
                            colSelectors.push((await (0, _genRollupSelectv2.default)({
                                baseModelSqlv2: baseModel,
                                knex: baseModel.dbDriver,
                                columnOptions: await column.getColOptions(baseModel.context)
                            })).builder.as((0, _dbHelpers.getAs)(column)));
                            groupBySelectors.push((0, _dbHelpers.getAs)(column));
                            break;
                        case _nocodbsdk.UITypes.Formula:
                            {
                                let selectQb;
                                try {
                                    const _selectQb = await baseModel.getSelectQueryBuilderForFormula(column);
                                    selectQb = baseModel.dbDriver.raw(`?? as ??`, [
                                        _selectQb.builder,
                                        (0, _dbHelpers.getAs)(column)
                                    ]);
                                } catch (e) {
                                    console.log(e);
                                    selectQb = baseModel.dbDriver.raw(`'ERR' as ??`, [
                                        (0, _dbHelpers.getAs)(column)
                                    ]);
                                }
                                colSelectors.push(selectQb);
                                groupBySelectors.push((0, _dbHelpers.getAs)(column));
                                break;
                            }
                        case _nocodbsdk.UITypes.Lookup:
                        case _nocodbsdk.UITypes.LinkToAnotherRecord:
                            {
                                const _selectQb = await (0, _generateLookupSelectQuery.default)({
                                    baseModelSqlv2: baseModel,
                                    column,
                                    alias: null,
                                    model: baseModel.model,
                                    getAlias
                                });
                                const selectQb = baseModel.dbDriver.raw(`?? as ??`, [
                                    baseModel.dbDriver.raw(_selectQb.builder).wrap('(', ')'),
                                    (0, _dbHelpers.getAs)(column)
                                ]);
                                colSelectors.push(selectQb);
                                groupBySelectors.push((0, _dbHelpers.getAs)(column));
                                break;
                            }
                        case _nocodbsdk.UITypes.DateTime:
                        case _nocodbsdk.UITypes.CreatedTime:
                        case _nocodbsdk.UITypes.LastModifiedTime:
                            {
                                const columnName = await (0, _dbHelpers.getColumnName)(baseModel.context, column, columns);
                                // ignore seconds part in datetime and group
                                if (baseModel.dbDriver.clientType() === 'pg') {
                                    colSelectors.push(baseModel.dbDriver.raw("date_trunc('minute', ??) + interval '0 seconds' as ??", [
                                        columnName,
                                        (0, _dbHelpers.getAs)(column)
                                    ]));
                                } else if (baseModel.dbDriver.clientType() === 'mysql' || baseModel.dbDriver.clientType() === 'mysql2') {
                                    colSelectors.push(// baseModel.dbDriver.raw('??::date as ??', [columnName, getAs(column)]),
                                    baseModel.dbDriver.raw("DATE_SUB(CONVERT_TZ(??, @@GLOBAL.time_zone, '+00:00'), INTERVAL SECOND(??) SECOND) as ??", [
                                        columnName,
                                        columnName,
                                        (0, _dbHelpers.getAs)(column)
                                    ]));
                                } else if (baseModel.dbDriver.clientType() === 'sqlite3') {
                                    colSelectors.push(baseModel.dbDriver.raw(`strftime ('%Y-%m-%d %H:%M:00',:column:) ||
  (
   CASE WHEN substr(:column:, 20, 1) = '+' THEN
    printf ('+%s:',
     substr(:column:, 21, 2)) || printf ('%s',
     substr(:column:, 24, 2))
   WHEN substr(:column:, 20, 1) = '-' THEN
    printf ('-%s:',
     substr(:column:, 21, 2)) || printf ('%s',
     substr(:column:, 24, 2))
   ELSE
    '+00:00'
   END) AS :id:`, {
                                        column: columnName,
                                        id: (0, _dbHelpers.getAs)(column)
                                    }));
                                } else {
                                    colSelectors.push(baseModel.dbDriver.raw('DATE(??) as ??', [
                                        columnName,
                                        (0, _dbHelpers.getAs)(column)
                                    ]));
                                }
                                groupBySelectors.push((0, _dbHelpers.getAs)(column));
                            }
                            break;
                        case _nocodbsdk.UITypes.UUID:
                            {
                                // PR review fix #2: use shared helper to cast UUID to text on PG
                                const columnName = await (0, _dbHelpers.getColumnName)(baseModel.context, column, columns);
                                colSelectors.push(buildUuidGroupBySelector({
                                    baseModel,
                                    columnName,
                                    alias: (0, _dbHelpers.getAs)(column)
                                }));
                                groupBySelectors.push((0, _dbHelpers.getAs)(column));
                                break;
                            }
                        default:
                            {
                                const columnName = await (0, _dbHelpers.getColumnName)(baseModel.context, column, columns);
                                colSelectors.push(baseModel.dbDriver.raw('?? as ??', [
                                    columnName,
                                    (0, _dbHelpers.getAs)(column)
                                ]));
                                groupBySelectors.push((0, _dbHelpers.getAs)(column));
                                break;
                            }
                    }
                }));
                // get aggregated count of each group
                tQb.count(`${((_baseModel_model_primaryKey = baseModel.model.primaryKey) == null ? void 0 : _baseModel_model_primaryKey.column_name) || '*'} as count`);
                tQb.select(...colSelectors);
                if (+(rest == null ? void 0 : rest.shuffle)) {
                    await baseModel.shuffle({
                        qb: tQb
                    });
                }
                await (0, _conditionV2.default)(baseModel, [
                    ...baseModel.viewId ? [
                        new _models.Filter({
                            children: viewFilterList || [],
                            is_group: true
                        })
                    ] : [],
                    new _models.Filter({
                        children: rest.filterArr || [],
                        is_group: true,
                        logical_op: 'and'
                    }),
                    new _models.Filter({
                        children: (0, _nocodbsdk.extractFilterFromXwhere)(baseModel.context, where, aliasColObjMap).filters,
                        is_group: true,
                        logical_op: 'and'
                    }),
                    new _models.Filter({
                        children: groupFilter,
                        is_group: true,
                        logical_op: 'and'
                    }),
                    new _models.Filter({
                        children: args.filterArr || [],
                        is_group: true,
                        logical_op: 'and'
                    })
                ], tQb);
                if (!groupSort) {
                    var _rest_sortArr;
                    if ((_rest_sortArr = rest.sortArr) == null ? void 0 : _rest_sortArr.length) {
                        groupSort = rest.sortArr;
                    } else if (baseModel.viewId) {
                        groupSort = await _models.Sort.list(baseModel.context, {
                            viewId: baseModel.viewId
                        });
                    }
                }
                for (const sort of groupSort || []){
                    if (!groupByColumns[sort.fk_column_id]) {
                        continue;
                    }
                    const column = groupByColumns[sort.fk_column_id];
                    if ([
                        _nocodbsdk.UITypes.User,
                        _nocodbsdk.UITypes.CreatedBy,
                        _nocodbsdk.UITypes.LastModifiedBy
                    ].includes(column.uidt)) {
                        const columnName = await (0, _dbHelpers.getColumnName)(baseModel.context, column, columns);
                        const columnNameQb = sqlNullIfBlank({
                            columnName,
                            baseModel,
                            isStringType: true
                        });
                        const baseUsers = await _models.BaseUser.getUsersList(baseModel.context, {
                            base_id: column.base_id,
                            include_internal_user: true
                        });
                        // create nested replace statement for each user
                        const finalStatement = baseUsers.reduce((acc, user)=>{
                            const qb = baseModel.dbDriver.raw(`REPLACE(${acc}, ?, ?)`, [
                                user.id,
                                user.display_name || user.email
                            ]);
                            return qb.toQuery();
                        }, columnNameQb.toQuery());
                        if (![
                            'asc',
                            'desc'
                        ].includes(sort.direction)) {
                            tQb.orderBy('count', sort.direction === 'count-desc' ? 'desc' : 'asc', sort.direction === 'count-desc' ? 'LAST' : 'FIRST');
                        } else {
                            tQb.orderBy((0, _sqlSanitize.sanitize)(baseModel.dbDriver.raw(finalStatement)), sort.direction, sort.direction === 'desc' ? 'LAST' : 'FIRST');
                        }
                    } else {
                        if (![
                            'asc',
                            'desc'
                        ].includes(sort.direction)) {
                            tQb.orderBy('count', sort.direction === 'count-desc' ? 'desc' : 'asc', sort.direction === 'count-desc' ? 'LAST' : 'FIRST');
                        } else {
                            tQb.orderBy((0, _dbHelpers.getAs)(column), sort.direction, sort.direction === 'desc' ? 'LAST' : 'FIRST');
                        }
                    }
                    tQb.groupBy(...groupBySelectors);
                    (0, _dbHelpers.applyPaginate)(tQb, rest);
                }
                let subQuery;
                switch(baseModel.dbDriver.client.config.client){
                    case 'pg':
                        subQuery = baseModel.dbDriver.select(baseModel.dbDriver.raw(`json_agg(json_build_object('count', "count", '${rest.column_name}', "${colIds}")) as ??`, [
                            getAlias()
                        ])).from(tQb.as(getAlias()));
                        selectors.push(baseModel.dbDriver.raw(`(??) as ??`, [
                            subQuery,
                            `${f.alias}`
                        ]));
                        break;
                    case 'mysql2':
                        subQuery = baseModel.dbDriver.select(baseModel.dbDriver.raw(`JSON_ARRAYAGG(JSON_OBJECT('count', \`count\`, '${rest.column_name}', \`${colIds}\`))`)).from(baseModel.dbDriver.raw(`(??) as ??`, [
                            tQb,
                            getAlias()
                        ]));
                        selectors.push(baseModel.dbDriver.raw(`(??) as ??`, [
                            subQuery,
                            f.alias
                        ]));
                        break;
                    case 'sqlite3':
                        subQuery = baseModel.dbDriver.select(baseModel.dbDriver.raw(`json_group_array(json_object('count', "count", '${rest.column_name}', "${colIds}")) as ??`, [
                            f.alias
                        ])).from(tQb.as(getAlias()));
                        selectors.push(baseModel.dbDriver.raw(`(??) as ??`, [
                            subQuery,
                            f.alias
                        ]));
                        break;
                    default:
                        _catchError.NcError.get(baseModel.context).notImplemented('This database does not support bulk groupBy');
                }
            }
            const qb = baseModel.dbDriver(baseModel.tnPath);
            qb.select(...selectors).limit(1);
            return await baseModel.execAndParse(qb, null, {
                raw: true,
                first: true
            });
        } catch (err) {
            logger.log(err);
            return [];
        }
    };
    const bulkAggregate = async (args, bulkFilterList, view)=>{
        try {
            if (!(bulkFilterList == null ? void 0 : bulkFilterList.length)) {
                return {};
            }
            const { where, aggregation } = baseModel._getListArgs(args);
            const columns = await baseModel.model.getColumns(baseModel.context);
            let viewColumns;
            if (baseModel.viewId) {
                viewColumns = (await _models.GridViewColumn.list(baseModel.context, baseModel.viewId)).filter((c)=>{
                    const col = baseModel.model.columnsById[c.fk_column_id];
                    return c.show && ((view == null ? void 0 : view.show_system_fields) || !(0, _nocodbsdk.isSystemColumn)(col));
                });
                // By default, the aggregation is done based on the columns configured in the view
                // If the aggregation parameter is provided, only the columns mentioned in the aggregation parameter are considered
                // Also the aggregation type from the parameter is given preference over the aggregation type configured in the view
                if (aggregation == null ? void 0 : aggregation.length) {
                    viewColumns = viewColumns.map((c)=>{
                        const agg = aggregation.find((a)=>a.field === c.fk_column_id);
                        return new _models.GridViewColumn(_extends({}, c, {
                            show: !!agg,
                            aggregation: agg ? agg.type : c.aggregation
                        }));
                    }).filter((c)=>c.show);
                }
            } else {
                // If no viewId, use all model columns or those specified in aggregation
                if (aggregation == null ? void 0 : aggregation.length) {
                    viewColumns = aggregation.map((agg)=>{
                        const col = baseModel.model.columnsById[agg.field];
                        if (!col) return null;
                        return {
                            fk_column_id: col.id,
                            aggregation: agg.type,
                            show: true
                        };
                    }).filter(Boolean);
                } else {
                    viewColumns = [];
                }
            }
            const aliasColObjMap = await baseModel.model.getAliasColObjMap(baseModel.context, columns);
            const qb = baseModel.dbDriver(baseModel.tnPath);
            const aggregateExpressions = {};
            // Construct aggregate expressions for each view column
            for (const viewColumn of viewColumns){
                const col = baseModel.model.columnsById[viewColumn.fk_column_id];
                if (!col || !viewColumn.aggregation || (0, _nocodbsdk.isLinksOrLTAR)(col) && col.system) continue;
                const aliasFieldName = col.id;
                const aggSql = await (0, _aggregation.default)({
                    baseModelSqlv2: baseModel,
                    aggregation: viewColumn.aggregation,
                    column: col
                });
                if (aggSql) {
                    aggregateExpressions[aliasFieldName] = aggSql;
                }
            }
            if (!Object.keys(aggregateExpressions).length) {
                return {};
            }
            let viewFilterList = [];
            if (baseModel.viewId) {
                viewFilterList = await _models.Filter.rootFilterList(baseModel.context, {
                    viewId: baseModel.viewId
                });
            }
            const selectors = [];
            // Generate a knex raw query for each filter in the bulkFilterList
            for (const f of bulkFilterList){
                const tQb = baseModel.dbDriver(baseModel.tnPath);
                const { filters: aggFilter } = (0, _nocodbsdk.extractFilterFromXwhere)(baseModel.context, f.where, aliasColObjMap);
                let aggFilterJson = f.filterArrJson;
                try {
                    aggFilterJson = JSON.parse(aggFilterJson);
                } catch (_e) {}
                await (0, _conditionV2.default)(baseModel, [
                    ...baseModel.viewId ? [
                        new _models.Filter({
                            children: viewFilterList || [],
                            is_group: true
                        })
                    ] : [],
                    new _models.Filter({
                        children: args.filterArr || [],
                        is_group: true,
                        logical_op: 'and'
                    }),
                    new _models.Filter({
                        children: (0, _nocodbsdk.extractFilterFromXwhere)(baseModel.context, where, aliasColObjMap).filters,
                        is_group: true,
                        logical_op: 'and'
                    }),
                    new _models.Filter({
                        children: aggFilter,
                        is_group: true,
                        logical_op: 'and'
                    }),
                    ...aggFilterJson ? [
                        new _models.Filter({
                            children: aggFilterJson,
                            is_group: true
                        })
                    ] : []
                ], tQb);
                let jsonBuildObject;
                switch(baseModel.dbDriver.client.config.client){
                    case 'pg':
                        {
                            jsonBuildObject = baseModel.dbDriver.raw(`JSON_BUILD_OBJECT(${Object.keys(aggregateExpressions).map((key)=>{
                                return `'${key}', ${aggregateExpressions[key]}`;
                            }).join(', ')})`);
                            break;
                        }
                    case 'mysql2':
                        {
                            jsonBuildObject = baseModel.dbDriver.raw(`JSON_OBJECT(
                  ${Object.keys(aggregateExpressions).map((key)=>`'${key}', ${aggregateExpressions[key]}`).join(', ')})`);
                            break;
                        }
                    case 'sqlite3':
                        {
                            jsonBuildObject = baseModel.dbDriver.raw(`json_object(
                    ${Object.keys(aggregateExpressions).map((key)=>`'${key}', ${aggregateExpressions[key]}`).join(', ')})`);
                            break;
                        }
                    default:
                        _catchError.NcError.get(baseModel.context).notImplemented('This database is not supported for bulk aggregation');
                }
                tQb.select(jsonBuildObject);
                if (baseModel.dbDriver.client.config.client === 'mysql2') {
                    selectors.push(baseModel.dbDriver.raw('JSON_UNQUOTE(??) as ??', [
                        jsonBuildObject,
                        `${f.alias}`
                    ]));
                } else {
                    selectors.push(baseModel.dbDriver.raw('(??) as ??', [
                        tQb,
                        `${f.alias}`
                    ]));
                }
            }
            qb.select(...selectors);
            qb.limit(1);
            return await baseModel.execAndParse(qb, null, {
                first: true,
                bulkAggregate: true
            });
        } catch (err) {
            logger.log(err);
            return [];
        }
    };
    return {
        count,
        list,
        bulkCount,
        bulkList,
        bulkAggregate
    };
};
